import {
  Flex,
  ReportSection,
  ReportSectionItemContent,
  ReportSectionItemTable,
  type ReportTableCell,
  Text,
} from '@/components';
import type { LedgerKsFundBusinessInfoSectionProps } from '@/features/mold/models/ledger';
import {
  FundBusinessFormTitleModifier,
  fundBusinessForm,
} from '@/features/project/projects/detail/fund-bussiness/_model/fund-business';
import { useLocaledAmount } from '@/providers';
import { type AmountUnit, FALLBACK_TEXT, formatValueByUnit } from '@/utils';
import { groupBy } from 'lodash-es';
import type { CSSProperties, FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LEDGER_BG_COLOR,
  LEDGER_BORDER,
  LEDGER_FONT_SIZE,
} from '../../../utils';

export const KsFundBusinessInfoSection: FC<
  LedgerKsFundBusinessInfoSectionProps
> = ({ fiscalYear, fundBusinesses }) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const groupByList = groupBy(fundBusinesses, 'businessForm');
  const fundBusinessList = Object.entries(groupByList).map(
    ([businessForm, data]) => {
      return {
        businessForm: fundBusinessForm.parse(businessForm),
        data,
      };
    },
  );

  const minYearBoundary = 3;
  const minYear = fiscalYear ? fiscalYear - minYearBoundary : undefined;

  return (
    <ReportSection
      title={t('fund-business')}
      items={
        fundBusinessList.length > 0
          ? fundBusinessList.map((fundBusiness) => {
              return {
                subtitle: `${FundBusinessFormTitleModifier[fundBusiness.businessForm]}\n${t('report-amount-unit-description')}\n${t('ledger-fund-business-description')}`,
                item: (
                  <Flex vertical gap={8}>
                    {fundBusiness.data.map((data, dataIndex) => {
                      const isSubsidy = fundBusiness.businessForm === 'subsidy';

                      return (
                        <Flex
                          key={data.id}
                          vertical
                          style={{ breakInside: 'avoid' }}
                        >
                          <ReportSectionItemContent
                            value={data.title}
                            wrapperStyle={{ borderLeft: 'none' }}
                          />

                          <ReportSectionItemContent
                            isTable
                            value={
                              <ReportSectionItemTable
                                header={[
                                  {
                                    type: 'text',
                                    value: isSubsidy
                                      ? t('fiscal-year-of-issuance-decision')
                                      : t('actual-results-and-balance'),
                                    ratio: isSubsidy ? 0.55 : 1,
                                    textStyle: {
                                      fontSize: LEDGER_FONT_SIZE,
                                    },
                                  },
                                  {
                                    type: 'multiple',
                                    cell: {
                                      type: 'flex',
                                      vertical: true,
                                      flexCells: [
                                        {
                                          type: 'multiple',
                                          cell: {
                                            type: 'flex',
                                            flexCells: isSubsidy
                                              ? [
                                                  {
                                                    type: 'text',
                                                    value: t(
                                                      'decided-amount-info',
                                                    ),
                                                    align: 'center',
                                                    textStyle: {
                                                      fontSize:
                                                        LEDGER_FONT_SIZE,
                                                    },
                                                  },
                                                  {
                                                    type: 'text',
                                                    value:
                                                      t('expenditure-year'),
                                                    align: 'center',
                                                    textStyle: {
                                                      fontSize:
                                                        LEDGER_FONT_SIZE,
                                                    },
                                                  },
                                                ]
                                              : [],
                                            cellRatios: [2, 5],
                                          },
                                        },
                                        {
                                          type: 'multiple',
                                          cell: {
                                            type: 'flex',
                                            flexCells: isSubsidy
                                              ? [
                                                  {
                                                    type: 'text',
                                                    value: t('fund-business'),
                                                    align: 'center',
                                                    textStyle: {
                                                      fontSize:
                                                        LEDGER_FONT_SIZE,
                                                    },
                                                  },
                                                  {
                                                    type: 'text',
                                                    value: t('estimate'),
                                                    align: 'center',
                                                    textStyle: {
                                                      fontSize:
                                                        LEDGER_FONT_SIZE,
                                                    },
                                                  },
                                                  ...((
                                                    data.performances[0]
                                                      ?.yearAmountInfos ?? []
                                                  ).map((_, columnIndex) => {
                                                    if (
                                                      !minYear ||
                                                      !fiscalYear
                                                    ) {
                                                      return {
                                                        type: 'text',
                                                        value: '',
                                                      };
                                                    }
                                                    const year =
                                                      minYear + columnIndex;
                                                    const isEstimatedYear =
                                                      year >= fiscalYear;
                                                    const isEstimatedYearOverFiscalYear =
                                                      year > fiscalYear;

                                                    return {
                                                      type: 'text',
                                                      value:
                                                        isEstimatedYearOverFiscalYear
                                                          ? t(
                                                              'estimate-over-current-fiscal-year-with-year',
                                                              { year },
                                                            )
                                                          : isEstimatedYear
                                                            ? t(
                                                                'estimate-with-year',
                                                                { year },
                                                              )
                                                            : year,
                                                      align: 'center',
                                                      textStyle: {
                                                        fontSize:
                                                          LEDGER_FONT_SIZE,
                                                      },
                                                    };
                                                  }) satisfies ReportTableCell[]),
                                                ]
                                              : ((
                                                  data.performances[0]
                                                    ?.yearAmountInfos ?? []
                                                ).map((_, columnIndex) => {
                                                  if (!minYear || !fiscalYear) {
                                                    return {
                                                      type: 'text',
                                                      value: '',
                                                    };
                                                  }

                                                  const year =
                                                    minYear + columnIndex;
                                                  const isEstimatedYear =
                                                    year >= fiscalYear;
                                                  const isEstimatedYearOverFiscalYear =
                                                    year > fiscalYear;

                                                  return {
                                                    type: 'text',
                                                    value:
                                                      isEstimatedYearOverFiscalYear ||
                                                      isEstimatedYear
                                                        ? t(
                                                            'estimate-with-year',
                                                            { year },
                                                          )
                                                        : year,
                                                    align: 'center',
                                                    textStyle: {
                                                      fontSize:
                                                        LEDGER_FONT_SIZE,
                                                    },
                                                  };
                                                }) satisfies ReportTableCell[]),
                                          },
                                        },
                                      ],
                                    },
                                    ratio:
                                      data.performances[0]?.yearAmountInfos
                                        .length ?? 0,
                                    wrapperStyle: {
                                      padding: 0,
                                    },
                                  },
                                ]}
                                data={data.performances.map(
                                  (performance, index) => {
                                    if (!minYear || !fiscalYear) {
                                      return {
                                        row: [
                                          {
                                            type: 'text',
                                            value: '',
                                          },
                                        ] as ReportTableCell[],
                                      };
                                    }
                                    const year = minYear + index;
                                    const isEstimatedYear = year >= fiscalYear;
                                    const tableCellBorderBottom =
                                      index === data.performances.length - 1 &&
                                      dataIndex === fundBusiness.data.length - 1
                                        ? 0
                                        : LEDGER_BORDER;

                                    return {
                                      row: isSubsidy
                                        ? ([
                                            {
                                              type: 'text',
                                              value: isEstimatedYear
                                                ? t('estimate-with-year', {
                                                    year,
                                                  })
                                                : t('fund-business-with-year', {
                                                    year,
                                                  }),
                                              wrapperStyle: {
                                                backgroundColor:
                                                  LEDGER_BG_COLOR,
                                                borderBottom:
                                                  tableCellBorderBottom,
                                              },
                                            },
                                            {
                                              type: 'multiple',
                                              cell: {
                                                type: 'flex',
                                                flexCells: [
                                                  ...getCountAmountTableCell({
                                                    countAmountInfo:
                                                      !isEstimatedYear
                                                        ? performance.decidedAmountInfo
                                                        : null,
                                                    unit,
                                                  }),
                                                  ...getCountAmountTableCell({
                                                    countAmountInfo:
                                                      performance.estimatedAmountInfo,
                                                    unit,
                                                  }),
                                                  ...performance.yearAmountInfos.flatMap(
                                                    (info) => {
                                                      return getCountAmountTableCell(
                                                        {
                                                          countAmountInfo:
                                                            info.amountInfo,
                                                          unit,
                                                        },
                                                      );
                                                    },
                                                  ),
                                                ],
                                              },
                                              wrapperStyle: {
                                                padding: 0,
                                                borderBottom:
                                                  tableCellBorderBottom,
                                              },
                                            },
                                          ] satisfies ReportTableCell[])
                                        : ([
                                            {
                                              type: 'text',
                                              value: performance.title,
                                              wrapperStyle: {
                                                backgroundColor:
                                                  LEDGER_BG_COLOR,
                                                borderBottom:
                                                  index ===
                                                    data.performances.length -
                                                      1 &&
                                                  dataIndex ===
                                                    fundBusiness.data.length - 1
                                                    ? 0
                                                    : LEDGER_BORDER,
                                              },
                                            },
                                            {
                                              type: 'multiple',
                                              cell: {
                                                type: 'flex',
                                                flexCells:
                                                  performance.yearAmountInfos.flatMap(
                                                    (info) => {
                                                      return getCountAmountTableCell(
                                                        {
                                                          countAmountInfo:
                                                            info.amountInfo,
                                                          unit,
                                                        },
                                                      );
                                                    },
                                                  ),
                                              },
                                              wrapperStyle: {
                                                padding: 0,
                                                borderBottom:
                                                  index ===
                                                    data.performances.length -
                                                      1 &&
                                                  dataIndex ===
                                                    fundBusiness.data.length - 1
                                                    ? 0
                                                    : LEDGER_BORDER,
                                              },
                                            },
                                          ] satisfies ReportTableCell[]),
                                    };
                                  },
                                )}
                              />
                            }
                          />
                        </Flex>
                      );
                    })}
                  </Flex>
                ),
              };
            })
          : [
              {
                subtitle: FALLBACK_TEXT,
                item: (
                  <ReportSectionItemContent
                    value={null}
                    wrapperStyle={{ height: '100%', borderBottom: 'none' }}
                  />
                ),
              },
            ]
      }
    />
  );
};

const getCountAmountTableCell = ({
  countAmountInfo,
  unit,
  wrapperStyle,
}: {
  countAmountInfo:
    | { count: number | null; amount: number | null }
    | null
    | undefined;
  unit: AmountUnit;
  wrapperStyle?: CSSProperties;
}): ReportTableCell[] => {
  return [
    {
      type: 'element',
      element: (
        <Flex
          justify="space-between"
          align="center"
          wrap="wrap"
          style={{
            padding: '0 8px',
          }}
        >
          <Text
            text={`(${formatValueByUnit(countAmountInfo?.count)})`}
            variant="footnote"
          />
          <Text
            text={formatValueByUnit(countAmountInfo?.amount, {
              operation: 'round',
              unit,
            })}
            variant="footnote"
          />
        </Flex>
      ),
      wrapperStyle,
    },
  ];
};
